import React, { useState, useEffect } from 'react';
import { taggingAPI, projectAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { 
  UserCheck, Clock, X, Check, Plus, Search, Phone, Calendar,
  AlertTriangle, RefreshCw, Shield, User, Building2, Users,
  MapPin, IndianRupee, FileText, CheckCircle2
} from 'lucide-react';

const ClientTaggingPage = () => {
  const { user, hasRole } = useAuth();
  const [taggings, setTaggings] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [rejectDialog, setRejectDialog] = useState({ open: false, id: null, reason: '' });

  const [newTagging, setNewTagging] = useState({
    project_id: '',
    client_name: '',
    client_phone: '',
    bhk_preference: '',
    budget: '',
    remarks: '',
  });

  const isPartner = hasRole(['partner', 'sub_user']);
  const canApprove = hasRole(['builder_admin', 'sales_team']);
  const canCreate = hasRole(['partner', 'sub_user']);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [taggingsRes, projectsRes] = await Promise.all([
        taggingAPI.list(),
        projectAPI.list(),
      ]);
      setTaggings(taggingsRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTagging = async (e) => {
    e.preventDefault();
    try {
      await taggingAPI.create({
        ...newTagging,
        budget: newTagging.budget ? parseFloat(newTagging.budget) : null,
      });
      toast.success('Client tagging request submitted for approval');
      setDialogOpen(false);
      setNewTagging({ project_id: '', client_name: '', client_phone: '', bhk_preference: '', budget: '', remarks: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit tagging');
    }
  };

  const handleApprove = async (id) => {
    try {
      await taggingAPI.approve(id);
      toast.success('Client tagging approved! Client is now locked for 90 days.');
      loadData();
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  const handleReject = async () => {
    if (!rejectDialog.id) return;
    try {
      await taggingAPI.reject(rejectDialog.id, rejectDialog.reason || 'Rejected by admin');
      toast.success('Tagging request rejected');
      setRejectDialog({ open: false, id: null, reason: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to reject');
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      'pending': { class: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock, label: 'Pending Approval' },
      'approved': { class: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2, label: 'Approved' },
      'rejected': { class: 'bg-red-100 text-red-700 border-red-200', icon: X, label: 'Rejected' },
      'expired': { class: 'bg-gray-100 text-gray-600 border-gray-200', icon: AlertTriangle, label: 'Expired' },
    };
    return configs[status] || configs.pending;
  };

  const getDaysRemaining = (expiryDate) => {
    if (!expiryDate) return null;
    const expiry = new Date(expiryDate);
    const now = new Date();
    const days = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return null;
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)} L`;
    return `₹${amount.toLocaleString()}`;
  };

  const filteredTaggings = taggings.filter((t) => {
    const matchesSearch = 
      t.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.client_phone?.includes(searchTerm) ||
      t.partner?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.project?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTab = activeTab === 'all' || t.status === activeTab;
    return matchesSearch && matchesTab;
  });

  const stats = {
    total: taggings.length,
    pending: taggings.filter(t => t.status === 'pending').length,
    approved: taggings.filter(t => t.status === 'approved').length,
    expiringSoon: taggings.filter(t => {
      const days = getDaysRemaining(t.expiry_date);
      return t.status === 'approved' && days !== null && days <= 14;
    }).length,
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-tagging-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-7 w-7 text-amber-600" />
            Client Tagging
          </h1>
          <p className="text-muted-foreground">3-month exclusivity protection for channel partners</p>
        </div>
        
        {canCreate && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="btn-tag-client">
                <Plus className="h-4 w-4 mr-2" />
                Tag New Client
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Tag a New Client</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateTagging} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Project *</Label>
                  <Select 
                    value={newTagging.project_id} 
                    onValueChange={(v) => setNewTagging({...newTagging, project_id: v})} 
                    required
                  >
                    <SelectTrigger data-testid="select-project">
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                            {p.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Client Name *</Label>
                  <Input 
                    value={newTagging.client_name} 
                    onChange={(e) => setNewTagging({...newTagging, client_name: e.target.value})} 
                    required 
                    placeholder="Enter client's full name"
                    data-testid="input-client-name"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Client Phone *</Label>
                  <Input 
                    value={newTagging.client_phone} 
                    onChange={(e) => setNewTagging({...newTagging, client_phone: e.target.value})} 
                    required 
                    placeholder="+91 9876543210"
                    data-testid="input-client-phone"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>BHK Preference</Label>
                    <Select 
                      value={newTagging.bhk_preference} 
                      onValueChange={(v) => setNewTagging({...newTagging, bhk_preference: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select BHK" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1 BHK">1 BHK</SelectItem>
                        <SelectItem value="2 BHK">2 BHK</SelectItem>
                        <SelectItem value="3 BHK">3 BHK</SelectItem>
                        <SelectItem value="4 BHK">4 BHK</SelectItem>
                        <SelectItem value="Penthouse">Penthouse</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Budget (₹)</Label>
                    <Input 
                      type="number" 
                      value={newTagging.budget} 
                      onChange={(e) => setNewTagging({...newTagging, budget: e.target.value})} 
                      placeholder="e.g., 5000000"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Remarks / Notes</Label>
                  <Textarea 
                    value={newTagging.remarks} 
                    onChange={(e) => setNewTagging({...newTagging, remarks: e.target.value})} 
                    placeholder="Any additional notes about this client..."
                    rows={3}
                  />
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                  <p className="font-medium text-amber-800 flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    90-Day Exclusivity Protection
                  </p>
                  <p className="text-amber-700 mt-1">
                    Once approved, this client will be exclusively assigned to you for 90 days. 
                    No other partner can tag or claim this client during this period.
                  </p>
                </div>
                
                <Button type="submit" className="w-full" data-testid="btn-submit-tagging">
                  Submit for Approval
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <UserCheck className="h-4 w-4" />
              <span className="text-sm">Total Tagged</span>
            </div>
            <p className="text-2xl font-bold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-amber-600 mb-1">
              <Clock className="h-4 w-4" />
              <span className="text-sm">Pending Approval</span>
            </div>
            <p className="text-2xl font-bold text-amber-700">{stats.pending}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-emerald-600 mb-1">
              <CheckCircle2 className="h-4 w-4" />
              <span className="text-sm">Active (Approved)</span>
            </div>
            <p className="text-2xl font-bold text-emerald-700">{stats.approved}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-orange-600 mb-1">
              <AlertTriangle className="h-4 w-4" />
              <span className="text-sm">Expiring Soon</span>
            </div>
            <p className="text-2xl font-bold text-orange-700">{stats.expiringSoon}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder="Search by client, partner, or project..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
            className="pl-10"
            data-testid="search-taggings"
          />
        </div>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all">All ({taggings.length})</TabsTrigger>
            <TabsTrigger value="pending">Pending ({stats.pending})</TabsTrigger>
            <TabsTrigger value="approved">Approved ({stats.approved})</TabsTrigger>
            <TabsTrigger value="rejected">Rejected</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Taggings List */}
      {filteredTaggings.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <UserCheck className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">No client taggings found</p>
            <p className="text-muted-foreground">
              {canCreate ? 'Tag your first client to protect them for 90 days' : 'No tagging requests to review'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredTaggings.map((tagging) => {
            const daysRemaining = getDaysRemaining(tagging.expiry_date);
            const statusConfig = getStatusConfig(tagging.status);
            const StatusIcon = statusConfig.icon;
            
            return (
              <Card 
                key={tagging.id} 
                className={`${tagging.status === 'pending' ? 'border-amber-200 bg-amber-50/30' : ''} 
                           ${daysRemaining !== null && daysRemaining <= 14 ? 'border-orange-200 bg-orange-50/30' : ''}`}
              >
                <CardContent className="p-4">
                  <div className="flex flex-col lg:flex-row gap-4">
                    {/* Client Info */}
                    <div className="flex-1">
                      <div className="flex items-start gap-3">
                        <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                          <User className="h-6 w-6 text-slate-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-lg">{tagging.client_name}</h3>
                            <Badge className={statusConfig.class}>
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                            <Phone className="h-3 w-3" />
                            {tagging.client_phone}
                          </p>
                        </div>
                      </div>
                      
                      {/* Details Grid */}
                      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        {/* Channel Partner */}
                        <div className="flex items-start gap-2">
                          <Users className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-muted-foreground text-xs">Channel Partner</p>
                            <p className="font-medium">{tagging.partner?.name || 'Unknown'}</p>
                            {tagging.partner?.company && (
                              <p className="text-xs text-muted-foreground">{tagging.partner.company}</p>
                            )}
                          </div>
                        </div>
                        
                        {/* Project */}
                        <div className="flex items-start gap-2">
                          <Building2 className="h-4 w-4 text-violet-600 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-muted-foreground text-xs">Project</p>
                            <p className="font-medium">{tagging.project?.name || 'Unknown'}</p>
                            {tagging.project?.location && (
                              <p className="text-xs text-muted-foreground flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {tagging.project.location}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        {/* Preference & Budget */}
                        <div className="flex items-start gap-2">
                          <IndianRupee className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-muted-foreground text-xs">Preference</p>
                            <p className="font-medium">
                              {tagging.bhk_preference || '-'}
                              {tagging.budget && ` • ${formatCurrency(tagging.budget)}`}
                            </p>
                          </div>
                        </div>
                        
                        {/* Date Info */}
                        <div className="flex items-start gap-2">
                          <Calendar className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-muted-foreground text-xs">Tagged On</p>
                            <p className="font-medium">{formatDate(tagging.created_at)}</p>
                            {tagging.status === 'approved' && daysRemaining !== null && (
                              <p className={`text-xs font-medium ${daysRemaining <= 14 ? 'text-orange-600' : 'text-emerald-600'}`}>
                                {daysRemaining} days remaining
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Remarks */}
                      {tagging.remarks && (
                        <div className="mt-3 flex items-start gap-2 text-sm">
                          <FileText className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                          <p className="text-muted-foreground">{tagging.remarks}</p>
                        </div>
                      )}
                      
                      {/* Approval Info */}
                      {tagging.status === 'approved' && tagging.approved_by_user && (
                        <div className="mt-3 text-xs text-muted-foreground">
                          Approved by <span className="font-medium">{tagging.approved_by_user.name}</span> on {formatDate(tagging.approved_at)}
                        </div>
                      )}
                      
                      {/* Rejection Info */}
                      {tagging.status === 'rejected' && tagging.rejection_reason && (
                        <div className="mt-3 text-sm text-red-600 bg-red-50 rounded p-2">
                          <span className="font-medium">Rejection Reason:</span> {tagging.rejection_reason}
                        </div>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex flex-row lg:flex-col gap-2 justify-end">
                      {canApprove && tagging.status === 'pending' && (
                        <>
                          <Button 
                            size="sm" 
                            className="bg-emerald-600 hover:bg-emerald-700" 
                            onClick={() => handleApprove(tagging.id)}
                            data-testid={`btn-approve-${tagging.id}`}
                          >
                            <Check className="h-4 w-4 mr-1" /> Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-red-600 border-red-200 hover:bg-red-50" 
                            onClick={() => setRejectDialog({ open: true, id: tagging.id, reason: '' })}
                            data-testid={`btn-reject-${tagging.id}`}
                          >
                            <X className="h-4 w-4 mr-1" /> Reject
                          </Button>
                        </>
                      )}
                      
                      {tagging.status === 'approved' && daysRemaining !== null && daysRemaining <= 14 && isPartner && (
                        <Button size="sm" variant="outline">
                          <RefreshCw className="h-4 w-4 mr-1" /> Request Extension
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      
      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => setRejectDialog({ ...rejectDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Tagging Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Rejection Reason</Label>
              <Textarea 
                placeholder="Please provide a reason for rejection..."
                value={rejectDialog.reason}
                onChange={(e) => setRejectDialog({ ...rejectDialog, reason: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, id: null, reason: '' })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleReject}>
              Reject Tagging
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientTaggingPage;
